import { RadioSelectableCard, RadioSelectableCardProps, Typography } from '@qbe/ads-core';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Icon from '~/components-ds/Icon';
import Loading from '~/components-ds/Loading';
import { INDUSTRY_LIMIT } from '~/constants/domain-specific';
import ERRORS from '~/constants/errors';
import { useAppContent, useAppDispatch, useBusinessDetails, useOptionSelect } from '~/hooks';
import { useRTKQErrorHandler } from '~/hooks/useRTKQErrorHandler';
import { selectIsPropertyOwner } from '~/selectors/selectBusinessDetails';
import { selectIndustries } from '~/selectors/selectIndustries';
import { useGetIndustriesQuery } from '~/services/refData';
import { updateIndustry } from '~/slices/businessDetails';
import { hardDecline } from '~/slices/decline';
import { qalFormStartAction, qalTrackEventAction } from '~/thunks/analytics';
import { occupationSearchTypes } from '~/types/analytics';
import { Industry, UnderwritingStatus } from '~/types/refData';
import { industryCodeToIcon, utcDateFormatter } from '~/utils';
import { getActivePagePath } from '~/utils/analytics/helpers';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';

export interface IndustrySelectorProps {
  onSelect?: () => void;
}

const toOption = ({ industryCode: id, industryValue: value }: Industry) => ({
  id,
  value,
});

const IndustrySelector: React.FC<IndustrySelectorProps> = ({ onSelect }) => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.businessDetailsOccupancySearch',
  });

  const { industry, coverStart } = useBusinessDetails();
  const isPropertyOwner = useSelector(selectIsPropertyOwner);
  const dispatch = useAppDispatch();

  const { error, isLoading, isFetching } = useGetIndustriesQuery({
    effectiveDate: utcDateFormatter(coverStart!),
  });

  const industries = useSelector(selectIndustries);

  useRTKQErrorHandler(error);

  const options = useMemo(
    () =>
      industries
        ?.slice()
        .sort((a, b) => a.industryPriority - b.industryPriority)
        .slice(0, INDUSTRY_LIMIT)
        .map(toOption) ?? [],
    [industries]
  );

  const { selectedIDs, onSelectID, setSelectedIDs } = useOptionSelect({
    options,
  });

  useEffect(() => {
    // Wait for all necessary API responses to complete before firing the event.
    // If either the data is still loading (`isLoading`) or being fetched (`isFetching`),
    // do nothing and wait for the state to resolve.
    if (isLoading || isFetching) return;

    // Once the API responses are complete and the DOM has been updated with the new data,
    // dispatch the "optimize.activate" event using QAL (QBE Analytics Library).
    // This event notifies Webtrends (or similar analytics tools) that the page is ready
    // with updated dynamic content, ensuring accurate visibility tracking.
    dispatch(
      qalTrackEventAction({
        event: 'optimize.activate',
        data: {
          virtualPagePath: getActivePagePath(),
        },
      })
    );
  }, [isLoading, isFetching, dispatch]);

  const handleFormStart = useCallback(() => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  }, [dispatch]);

  const handleSelect = (id: string) => {
    onSelectID(id);
    const selectedIndustry = industries?.find((i) => i.industryCode === id);
    if (!selectedIndustry) {
      throw new Error('Industry not found');
    }
    // @TODO Jeff 09/11/0222 - Modify dispatch logic to dispatch value to redux storey before hard decline.
    handleFormStart();
    dispatch(
      selectedIDs.includes(id) ? updateIndustry(undefined) : updateIndustry(selectedIndustry)
    );
    if (
      !(isPropertyOwner && ['11', '12', '14'].includes(selectedIndustry.industryCode)) &&
      selectedIndustry.underwritingStatus === UnderwritingStatus.Decline
    ) {
      dispatch(
        hardDecline({
          ...ERRORS.industry,
          searchType: occupationSearchTypes.iconSearch,
        })
      );
    } else {
      onSelect?.();
    }
  };

  useEffect(() => {
    if (industry) {
      const option = options.find((o) => o?.id === toOption(industry)?.id);
      setSelectedIDs(option ? [option.id] : []);
    } else {
      setSelectedIDs([]);
    }
  }, [industries, isLoading, isFetching, industry, setSelectedIDs, options]);

  const radioList = useMemo(
    () =>
      options.map(({ id, value }) => ({
        label: value,
        id: `industry_${value}`,
        value: id,
        // TODO - Use @qbe/ui-qbe-icons
        customComponent: <Icon name={industryCodeToIcon[id] || 'other'} width={45} height={45} />,
        'data-track': 'Step 1 of 3: Select an industry',
        'data-track-value': 'capture',
      })) as RadioSelectableCardProps['radioList'],
    [options]
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <RadioSelectableCard
      title={getTextContent('fields.iconSearchIndustry.title')}
      disableBorder
      direction="horizontal"
      labelPlacement="top"
      titleSuffix={
        <ContextualHelpWithButton tracking="help: Step 1 of 3: Select an industry">
          <Typography>
            {isPropertyOwner
              ? getJSXContent('contextualhelpers.iconSearchIndustryLandlord.description')
              : getJSXContent('contextualhelpers.iconSearchIndustryBusinessOperator.description')}
          </Typography>
        </ContextualHelpWithButton>
      }
      name="industry"
      defaultValue={industry?.industryCode ?? ''}
      onChange={handleSelect}
      radioList={radioList}
    />
  );
};

export default IndustrySelector;
